import { useEffect, useState } from "react";
import { Banner, BlockStack, Button, Card, Collapsible, DataTable, FormLayout, Modal, Select, Text, TextField } from "@shopify/polaris";
import { ChevronDownIcon, ChevronUpIcon, DeleteIcon, EditIcon } from "@shopify/polaris-icons";

import { CardHeading } from "../../shared/components/CardHeading";
import { ImageThumbNailViewer, getPrimaryS3ImageKey } from "../../shared/components/s3_image_display/ImageThumbNailViewer";
import { S3ImageUploader } from "../../shared/components/s3_image_display/s3ImageUploader";
import { Toasts } from "../../shared/components/toasts/toasts";

const emptyMaterial = {
  id: null,
  name: "",
  package_type: "BOX",
  length_cm: "",
  width_cm: "",
  height_cm: "",
  image_s3_object_keys: "[]",
};

const typeOptions = [
  { label: "Box", value: "BOX" },
  { label: "Pallet", value: "PALLET" },
];

function authHeaders() {
  const currentUser = JSON.parse(localStorage.getItem("currentUser") || "{}");
  return new Headers({ Authorization: `Bearer ${currentUser.token || ""}`, "Content-Type": "application/json" });
}

async function packagingRequest(url, { method = "GET", body } = {}) {
  const response = await fetch(url, {
    method,
    headers: authHeaders(),
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload?.message || "Packaging request failed.");
  return payload;
}

function editableMaterial(material) {
  return {
    ...emptyMaterial,
    ...material,
    length_cm: String(material?.length_cm ?? ""),
    width_cm: String(material?.width_cm ?? ""),
    height_cm: String(material?.height_cm ?? ""),
    image_s3_object_keys: material?.image_s3_object_keys || "[]",
  };
}

export default function PackagingSettingsCard() {
  const [open, setOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [material, setMaterial] = useState(emptyMaterial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  async function loadMaterials() {
    try {
      setMaterials(await packagingRequest("/api/order-packaging/materials"));
      setError(null);
    } catch (requestError) {
      setError(requestError.message);
    }
  }

  useEffect(() => {
    loadMaterials();
  }, []);

  function showMaterial(nextMaterial = emptyMaterial) {
    setMaterial(editableMaterial(nextMaterial));
    setError(null);
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setMaterial(emptyMaterial);
  }

  async function saveMaterial() {
    setSaving(true);
    setError(null);
    try {
      await packagingRequest(material.id ? `/api/order-packaging/materials/${material.id}` : "/api/order-packaging/materials", {
        method: material.id ? "PATCH" : "POST",
        body: material,
      });
      closeModal();
      await loadMaterials();
      setToast("ok");
    } catch (requestError) {
      setError(requestError.message);
      setToast("error");
    } finally {
      setSaving(false);
    }
  }

  async function removeMaterial(item) {
    if (!window.confirm(`Remove “${item.name}” from reusable packaging? Existing orders will not be changed.`)) return;
    try {
      await packagingRequest(`/api/order-packaging/materials/${item.id}`, { method: "DELETE" });
      await loadMaterials();
      setToast("ok");
    } catch (requestError) {
      setError(requestError.message);
      setToast("error");
    }
  }

  const rows = materials.map((item) => {
    const imageKey = getPrimaryS3ImageKey(item.image_s3_object_keys);
    return [
      imageKey ? <ImageThumbNailViewer key={`packaging_image_${item.id}`} s3Key={imageKey} size={48} /> : <Text tone="subdued">No photo</Text>,
      item.name,
      item.package_type === "PALLET" ? "Pallet" : "Box",
      `${item.length_cm} × ${item.width_cm} × ${item.height_cm} cm`,
      <Button key={`edit_${item.id}`} icon={EditIcon} size="slim" onClick={() => showMaterial(item)}>Edit</Button>,
      <Button key={`remove_${item.id}`} icon={DeleteIcon} size="slim" tone="critical" variant="tertiary" onClick={() => removeMaterial(item)}>Remove</Button>,
    ];
  });

  return (
    <Card>
      <CardHeading
        title={
          <Button variant="tertiary" icon={open ? ChevronUpIcon : ChevronDownIcon} onClick={() => setOpen((current) => !current)}>
            <Text variant="headingMd" as="h2">Reusable packaging</Text>
          </Button>
        }
        actions={[{ content: "Add packaging", onAction: () => showMaterial(), variant: "secondary" }]}
      />

      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={material.id ? "Edit reusable packaging" : "Add reusable packaging"}
        primaryAction={{ content: "Save", onAction: saveMaterial, loading: saving }}
        secondaryActions={[{ content: "Cancel", onAction: closeModal }]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            {error && <Banner tone="critical"><p>{error}</p></Banner>}
            <FormLayout>
              <FormLayout.Group>
                <TextField label="Name" value={material.name} onChange={(value) => setMaterial((current) => ({ ...current, name: value }))} autoComplete="off" />
                <Select label="Type" options={typeOptions} value={material.package_type} onChange={(value) => setMaterial((current) => ({ ...current, package_type: value }))} />
              </FormLayout.Group>
              <FormLayout.Group condensed>
                <TextField label="Length (cm)" type="number" min={0.01} step="0.01" value={material.length_cm} onChange={(value) => setMaterial((current) => ({ ...current, length_cm: value }))} autoComplete="off" />
                <TextField label="Width (cm)" type="number" min={0.01} step="0.01" value={material.width_cm} onChange={(value) => setMaterial((current) => ({ ...current, width_cm: value }))} autoComplete="off" />
                <TextField label="Height (cm)" type="number" min={0.01} step="0.01" value={material.height_cm} onChange={(value) => setMaterial((current) => ({ ...current, height_cm: value }))} autoComplete="off" />
              </FormLayout.Group>
            </FormLayout>
            <S3ImageUploader
              S3ObjectKeys={material.image_s3_object_keys}
              S3ObjectNamePrefix="packaging_material"
              onUploadImages={(keys) => setMaterial((current) => ({ ...current, image_s3_object_keys: JSON.stringify(keys) }))}
              onRemoveImages={(keys) => setMaterial((current) => ({ ...current, image_s3_object_keys: JSON.stringify(keys) }))}
            />
          </BlockStack>
        </Modal.Section>
      </Modal>

      <Toasts toast={toast} setToast={setToast} toastContent="Packaging saved" errorToastContent="Packaging could not be saved" />

      <Collapsible open={open}>
        <BlockStack gap="200">
          {error && !modalOpen && <Banner tone="critical"><p>{error}</p></Banner>}
          {materials.length === 0 ? (
            <Text tone="subdued">No reusable packaging configured.</Text>
          ) : (
            <DataTable
              increasedTableDensity
              verticalAlign="middle"
              columnContentTypes={["text", "text", "text", "text", "text", "text"]}
              headings={[<b>Photo</b>, <b>Name</b>, <b>Type</b>, <b>Dimensions</b>, <b>Edit</b>, <b>Remove</b>]}
              rows={rows}
            />
          )}
        </BlockStack>
      </Collapsible>
    </Card>
  );
}
